import { inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { PaginatorState } from "primeng/paginator";
import { take } from "rxjs";

export abstract class AbstractPaginableComponent {
  protected readonly router = inject(Router);
  protected readonly activatedRoute = inject(ActivatedRoute);

  pageSize = 25;
  actualPage = 0;
  pageLoading = false;
  totalRecords = 0;
  firstItemOnPageIndex = 0;

  abstract loadItems(firstItemIndex: number): Promise<void>;

  async onPageChange(event: PaginatorState): Promise<void> {
    if (event.page === this.actualPage) {
      return;
    }
    this.firstItemOnPageIndex = event.first ?? 0;
    this.actualPage = event.page ?? 0;
    await this.loadItems(this.firstItemOnPageIndex);
  }

  async loadFirstPage(): Promise<void> {
    this.actualPage = 0;
    this.firstItemOnPageIndex = 0;
    await this.loadItems(this.firstItemOnPageIndex);
  }

  async onPage(event: { first: number; rows: number }): Promise<void> {
    const page = Math.floor(event.first / event.rows);
    await this.updateRouteAndLoadPage(page);
  }

  loadCurrentPageFromRoute(): void {
    // eslint-disable-next-line rxjs-angular/prefer-takeuntil
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((params) => {
      const page = Number(params["page"]);
      if (page !== null && !isNaN(page)) {
        this.actualPage = page;
      } else {
        this.actualPage = 0;
        void this.updateRoutePage(this.actualPage);
      }
      void this.loadActualPage(this.actualPage);
    });
  }

  async loadActualPage(page: number): Promise<void> {
    this.actualPage = page;
    this.firstItemOnPageIndex = page * this.pageSize;
    await this.loadItems(this.firstItemOnPageIndex);
  }

  async updateRouteAndLoadPage(page: number): Promise<void> {
    void this.updateRoutePage(page);
    await this.loadActualPage(page);
  }

  async updateRoutePage(page: number): Promise<void> {
    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page },
      queryParamsHandling: "merge",
    });
  }
}
